import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

/** Matches `summary` from quotation evaluation APIs (detail + list). */
export type QuotationEvaluationSummaryByQuote = {
  slot: number;
  quotationId: number;
  supplier: { name: string; code?: string | null } | null;
  ref: string | null;
  currency: { code: string; symbol?: string | null } | null;
  lineItemsSubtotal: number;
  lineItemsSubtotalUsd: number | null;
  expensesSubtotal: number;
  expensesSubtotalUsd: number | null;
  grandTotal: number;
  grandTotalUsd: number | null;
  isLowestTotalUsd: boolean;
};

export type QuotationEvaluationSummary = {
  byQuote: QuotationEvaluationSummaryByQuote[];
  recommendedSlot: number | null;
  canCompareInUsd: boolean;
  fxNote: string | null;
};

export interface QuotationEvaluation {
  id: number;
  ref?: string | null;
  evaluationDate?: string | null;
  notes?: string | null;
  caseId: number;
  quotation1Id?: number | null;
  quotation2Id?: number | null;
  quotation3Id?: number | null;
  quotation4Id?: number | null;
  quotation5Id?: number | null;
  createdAt?: string;
  updatedAt?: string;
  /** Present on GET /spare-quotation-evaluations/:id (and list when included by backend). */
  spareCase?:
    | {
        id: number;
        ref?: string | null;
        vessel?: { id: number; vesselName?: string | null; vesselAbrv?: string | null } | null;
      }
    | null;
  /** Per-supplier totals (list + detail). */
  summary?: QuotationEvaluationSummary | null;
}

export const useQuotationEvaluations = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<QuotationEvaluation> | QuotationEvaluation[]
  >(`/spare-quotation-evaluations${q}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<QuotationEvaluation>(data, page, limit);
  return {
    data: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: !!error,
    mutate,
  };
};

export function useQuotationEvaluation(id: number | undefined) {
  const key = id != null && id > 0 ? `/spare-quotation-evaluations/${id}` : null;
  const { data, error, isLoading, mutate } = useSWR(key, fetcher);
  return { item: data, error, isLoading, isError: !!error, revalidate: mutate };
}
